import React, { useState, useEffect, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaArrowLeft, FaGripLines, FaDownload } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import pdf from "../../../../Assets/Images/icons/pdf.png";
import { validateField } from "../../../../libs/validationUtils";
import Pagination from "../../../../libs/Pagination";
import { IoCloudUploadOutline } from "react-icons/io5";
import { AppContext } from "../../../../context/appContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import {
  getBible,
  addBible,
  updateBible,
  deleteBible,
  getSingleBibleBook,
  updateBibleBookSequence,
  deleteChapterAudio,
} from "../../../../libs/api/bibleApi";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../libs/LoadingSpinner";
import Swal from "sweetalert2";
import { AiOutlineEye } from "react-icons/ai";
import { getAllLanguages } from "../../../../libs/api/languageApi";
import { putFormDataApiCaller } from "../../../../libs/apiCaller";
import EditorUsfm from "./EditorUsfm";

export default function BibleBookList() {
  const { languageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const languageName = location.state?.languageName || 'Unknown Language';
  const { t, formatNum, isRTL } = useContext(AppContext);
  const [books, setBooks] = useState([]);
  const [category, setCategory] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedBookViewData, setSelectedBookViewData] = useState({});
  const [isViewDataLoading, setIsViewDataLoading] = useState(false);
  const [chapters, setChapters] = useState([]); // manage internal state

  const [usfmText, setUsfmText] = useState(
    "\\id GEN\n\\c 1\n\\v 1 In the beginning God created the heavens and the earth."
  );

  const [modalMode, setModalMode] = useState("add");
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("");
  const [description, setDescription] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [selectedBookId, setSelectedBookId] = useState(null);

  const [errors, setErrors] = useState({ title: "", pdfFile: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 5;
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [uploadingRows, setUploadingRows] = useState(new Set());

  const [languagesOptionsArr, setLanguagesOptionsArr] = useState([]);

  // Drag and drop state for book reordering
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [isUsfmEditorOpen, setIsUsfmEditorOpen] = useState(false);

  useEffect(() => {
    fetchBooks(true);
    getAllLanguagesFunc();
  }, [currentPage, limit, debouncedSearch, languageId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  const getSingleBibleBookAsyncFunc = async (id) => {
    setIsViewDataLoading(true);
    try {
      const response = await getSingleBibleBook(id);
      if (response?.data?.success) {
        setSelectedBookViewData(response?.data?.data || {});
        setChapters(response?.data?.data?.chapters || []);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
        setIsViewOpen(false);
      }
    } catch (error) {
      setIsViewOpen(false);
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsViewDataLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setIsViewOpen(false);
    setSelectedBookViewData({});
    setSelectedBookId(null);
  };

  // Helper function to get chapter display name
  const getChapterDisplayName = (chapterNumber) => {
    if (chapterNumber === 0) {
      return t('bible.introduction') || 'مقدمه';
    }
    return `${t('bible.chapter') || 'فصل'} ${formatNum(chapterNumber)}`;
  };

  const handleAudioUpload = async (index, file) => {
    const chapterNumber = chapters[index].chapterNumber;

    // Check if already uploading
    if (uploadingRows.has(index)) {
      return;
    }

    // Mark row as uploading
    setUploadingRows(prev => new Set(prev).add(index));

    // Upload to backend
    const formData = new FormData();
    formData.append("audio", file);
    try {
      const response = await putFormDataApiCaller(
        `bible/audio/${selectedBookId}/${chapterNumber}`,
        formData
      );
      if (response?.data?.success) {
        toast.success(response?.data?.message || t('bible.audioUploaded'));

        // Get the uploaded audio filename from response
        const uploadedAudio = response?.data?.data?.audio;
        const uploadedAudioName = response?.data?.data?.audioName;

        if (uploadedAudio) {
          // Extract just the filename (in case it has path)
          const audioFilename = uploadedAudio.includes('/')
            ? uploadedAudio.split('/').pop()
            : uploadedAudio;

          // Construct full audio URL matching backend format
          const fullAudioUrl = `https://microscoreapi.com/api/bibleAudio/${audioFilename}`;

          // Use functional state update to prevent race conditions
          setChapters(prevChapters => {
            const updatedChapters = [...prevChapters];
            updatedChapters[index] = {
              ...updatedChapters[index],
              audio: fullAudioUrl,
              audioName: uploadedAudioName || file.name
            };
            return updatedChapters;
          });
        }
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      // Remove row from uploading state
      setUploadingRows(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const handleAudioDelete = async (index) => {
    const chapterNumber = chapters[index].chapterNumber;
    const chapterDisplayName = getChapterDisplayName(chapterNumber);

    // Show confirmation dialog
    const result = await Swal.fire({
      title: t('bible.areYouSure'),
      text: `${t('bible.deleteAudioConfirm') || 'Delete audio for'} ${chapterDisplayName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t('bible.yesDelete'),
      cancelButtonText: t('common.cancel'),
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await deleteChapterAudio(selectedBookId, chapterNumber);
      if (response?.data?.success) {
        toast.success(response?.data?.message || t('bible.audioDeleted') || 'Audio deleted successfully');

        // Use functional state update to prevent race conditions
        setChapters(prevChapters => {
          const updatedChapters = [...prevChapters];
          updatedChapters[index] = {
            ...updatedChapters[index],
            audio: null,
            audioName: null
          };
          return updatedChapters;
        });
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handleAudioDownload = (index) => {
    const chapterNumber = chapters[index].chapterNumber;
    const audioName = chapters[index].audioName;

    if (!audioName) {
      toast.error(t('bible.noAudioToDownload') || 'No audio available to download');
      return;
    }

    // Create download URL
    const baseUrl = process.env.REACT_APP_PUBLIC_BASE_URL;
    const token = sessionStorage.getItem("token");
    const downloadUrl = `${baseUrl}bible/audio/download/${selectedBookId}/${chapterNumber}`;

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', audioName);

    // Set authorization header via fetch
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = audioName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        toast.error(t('bible.downloadFailed') || 'Failed to download audio');
        console.error('Download error:', error);
      });
  };

  const handleAudioDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(index);
  };

  const handleAudioDragLeave = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
  };

  const handleAudioDrop = async (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file type
      if (file.type === 'audio/mpeg' || file.type === 'audio/mp3' || file.name.endsWith('.mp3')) {
        await handleAudioUpload(index, file);
      } else {
        toast.error(t('bible.invalidAudioFileType') || 'Please upload an MP3 audio file');
      }
    }
  };

  const fetchBooks = async (isLoading) => {
    setInitialLoad(isLoading);
    try {
      const response = await getBible(currentPage, limit, debouncedSearch, languageId);
      if (response.status === 200) {
        // Add sequence number to each book for drag and drop
        const booksWithSequence = response.data.data.map((book, index) => ({
          ...book,
          sequence: book.sequence !== undefined ? book.sequence : index + 1
        }));
        setBooks(booksWithSequence || []);
        setTotalPages(response.data.totalPages || 1);
        setLimit(response.data.limit || 10);
        setCurrentPage(response.data.currentPage || 1);
        setTotalRecords(response.data.totalRecords || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setInitialLoad(false);
    }
  };

  const getSingleBibleBookFunc = async (id) => {
    setIsViewOpen(true);
    const bookId = Number(id) ? Number(id) : id;
    setSelectedBookId(bookId);

    await getSingleBibleBookAsyncFunc(bookId);
  };

  const getAllLanguagesFunc = async () => {
    try {
      const response = await getAllLanguages();
      if (response?.data?.success || response?.status === 200) {
        setLanguagesOptionsArr(response?.data?.data || []);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handleFieldChange = (name, value) => {
    if (name === "title") setTitle(value);
    if (name === "language") setLanguage(value);
    if (name === "pdfFile") setPdfFile(value);
    // if (name === 'description') setDescription(value);
    if (name === "usfmText") setUsfmText(value);
    // Validate field
    const error = validateField(name, value.trim());
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    newErrors.title = validateField("title", title.trim());
    newErrors.usfmText = validateField("usfmText", usfmText);
    setErrors(newErrors);

    // If there are errors, do not proceed
    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("language", language || languageId);
    formData.append("content", usfmText);
    formData.append("image", pdfFile);
    try {
      setLoading(true);
      const response = await addBible(formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("book_modal").close();

        toast.success(
          response?.data?.message || t('bible.bibleChapterAdded')
        );
        fetchBooks(true);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", usfmText);
    formData.append("language", language);
    if (pdfFile) {
      formData.append("image", pdfFile);
    }
    try {
      setLoading(true);
      const response = await updateBible(selectedBookId, formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("book_modal").close();
        toast.success(
          response?.data?.message || t('bible.bibleChapterUpdated')
        );
        fetchBooks(true);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    // Show confirmation dialog using SweetAlert
    const result = await Swal.fire({
      title: t('bible.areYouSure'),
      text: t('bible.cantRevert'),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t('bible.yesDelete'),
      cancelButtonText: t('common.cancel'),
    });

    if (!result.isConfirmed) {
      return; // User cancelled the deletion
    }

    try {
      const response = await deleteBible(id);
      if (response.status === 200 || response.status === 201) {
        toast.success(
          response?.data?.message || t('bible.bibleChapterDeleted')
        );
        fetchBooks(false);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Drag and drop handlers for book reordering
  const handleDragStart = (e, index) => {
    setDraggedItem(books[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    // Visual feedback
    const draggedElement = e.target.closest('tr');
    if (draggedElement) {
      draggedElement.style.opacity = '0.3';
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(books[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = books[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedBooks = books.map(book => {
      if (book._id === draggedItem._id) {
        return { ...book, sequence: targetSequence };
      }
      if (book._id === draggedOverItem._id) {
        return { ...book, sequence: draggedSequence };
      }
      return book;
    });

    // Sort by sequence to reflect the swap
    updatedBooks.sort((a, b) => a.sequence - b.sequence);

    setBooks(updatedBooks);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedUpdateSequence'));
      // Revert if update fails
      fetchBooks(false);
    }
  };

  const handleDragEnd = (e) => {
    // Restore opacity
    const draggedElement = e.target.closest('tr');
    if (draggedElement) {
      draggedElement.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      };
      const response = await updateBibleBookSequence(payload);
      if (response.status === 200 || response.status === 201) {
        // Success - no need to refetch as we've already updated UI optimistically
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
      throw error; // Re-throw to trigger refetch in handleDrop
    }
  };

  const openModal = (mode, book = null) => {
    setModalMode(mode);
    if (mode === "edit" && book) {
      setTitle(book.title);
      setUsfmText(book.content || book.description);
      setPdfFile(book.name);
      setSelectedBookId(book._id);
      setCategory(book.category || "");
      setCoverImage(book.cover || null);
      setLanguage(book.language?._id || book.language || languageId);
    } else {
      setTitle("");
      setPdfFile(null);
      setUsfmText(
        "\\id GEN\n\\c 1\n\\v 1 In the beginning God created the heavens and the earth."
      );
      setSelectedBookId(null);
      setCategory("");
      setCoverImage(null);
      setLanguage(languageId);
    }

    document.getElementById("book_modal").showModal();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidFileType =
        fileName.endsWith(".sfm") || fileName.endsWith(".usfm");
      if (isValidFileType) {
        setPdfFile(file);

        try {
          // Read the .sfm file as text instead of using PDF extraction
          const reader = new FileReader();
          reader.onload = (e) => {
            const text = e.target.result;
            setUsfmText(text);

            // Extract mt1 text from the loaded USFM content
            setTimeout(() => {
              extractmt1Text(text);
            }, 100);
          };
          reader.readAsText(file);

          const error = validateField("pdfFile", file);
          setErrors((prevErrors) => ({ ...prevErrors, pdfFile: error }));
        } catch (err) {
          toast.error(t('bible.failedToReadFile'));
        }
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          pdfFile: t('bible.invalidFileType'),
        }));
      }
    }
  };

  const clearForm = () => {
    setTitle("");
    setPdfFile(null);
    setUsfmText(
      "\\id GEN\n\\c 1\n\\v 1 In the beginning God created the heavens and the earth."
    );
    setErrors({ title: "", pdfFile: "" });
  };

  // Extract title text from USFM content
  const extractmt1Text = (usfmContent) => {
    try {
      // First, try to find h tag (header)
      const hMatch = usfmContent.match(/\\h\s+(.+?)(?=\\|\n|$)/);
      if (hMatch && hMatch[1]) {
        const hText = hMatch[1].trim();
        setTitle(hText);
        return hText;
      }

      // If h not found, try to find mt1 tag
      const mt1Match = usfmContent.match(/\\mt1\s+(.+?)(?=\\|\n|$)/);
      if (mt1Match && mt1Match[1]) {
        const mt1Text = mt1Match[1].trim();
        setTitle(mt1Text);
        return mt1Text;
      }

      // If mt1 not found, try to find mt tag
      const mtMatch = usfmContent.match(/\\mt\s+(.+?)(?=\\|\n|$)/);
      if (mtMatch && mtMatch[1]) {
        const mtText = mtMatch[1].trim();
        setTitle(mtText);
        return mtText;
      }

      // If mt not found, try to find imt or imt1 tag
      const imtMatch = usfmContent.match(/\\imt1?\s+(.+?)(?=\\|\n|$)/);
      if (imtMatch && imtMatch[1]) {
        const imtText = imtMatch[1].trim();
        setTitle(imtText);
        return imtText;
      }

      // Fallback: try to find any of these tags with different patterns
      const lines = usfmContent.split("\n");
      for (let line of lines) {
        if (line.includes("\\h")) {
          const hText = line.replace("\\h", "").trim();
          setTitle(hText);
          return hText;
        }
        if (line.includes("\\mt1")) {
          const mt1Text = line.replace("\\mt1", "").trim();
          setTitle(mt1Text);
          return mt1Text;
        }
        if (line.includes("\\mt")) {
          const mtText = line.replace("\\mt", "").trim();
          setTitle(mtText);
          return mtText;
        }
        if (line.includes("\\imt1") || line.includes("\\imt")) {
          const imtText = line.replace(/\\imt1?/, "").trim();
          setTitle(imtText);
          return imtText;
        }
      }

      return null;
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
      return null;
    }
  };

  // Call the function to extract mt1
  // extractmt1Text(usfmText);

  return (
    <div className="p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate('/admin/bible')}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
        >
          <FaArrowLeft size={18} />
          <span className="text-sm font-medium">{t('bible.title')}</span>
        </button>
        <span className="text-gray-400">/</span>
        <span className="text-sm font-semibold text-gray-800 capitalize">{languageName}</span>
      </div>

      <h2 className="text-2xl font-semibold mb-4">{languageName} - {t('bible.books') || 'Bible Books'}</h2>

      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Book */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('common.search')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2  border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('bible.addBook')}
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {initialLoad && (
            <div className="flex justify-center items-center h-full p-6">
              <LoadingSpinner />
            </div>
          )}
          {!initialLoad && books.length === 0 && (
            <div className="flex justify-center items-center h-full p-6">
              <p className="text-gray-500">{t('bible.noChapters')}</p>
            </div>
          )}
          {!initialLoad && books.length > 0 && (
            <table className="w-full text-left border-collapse">
              <thead className="bg-[#FAFAFA]">
                <tr className="text-sm font-[600]">
                  <th className={`p-2 py-4 border-b border-[#F7F7F9] w-12 ${isRTL ? 'pr-8' : 'pl-8'}`}>
                    {/* Drag handle column */}
                  </th>
                  <th className={`p-2 py-4 border-b border-[#F7F7F9]`}>
                    {t('bible.id')}
                  </th>
                  <th className="p-2 py-4 border-b border-[#F7F7F9]">{t('forms.title')}</th>
                  <th className="p-2 py-4 border-b border-[#F7F7F9]">
                    {t('bible.description')}
                  </th>
                  <th className="p-2 py-4 border-b border-[#F7F7F9]">{t('bible.chaptersCount') || 'Chapters'}</th>
                  <th className="p-2 py-4 border-b border-[#F7F7F9]">{t('bible.action')}</th>
                </tr>
              </thead>
              <tbody>
                {books.map((book, index) => (
                  <tr
                    key={book._id}
                    className={`hover:bg-gray-50 text-sm font-[500] ${
                      dragOverItem?._id === book._id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <td className={`p-2 border-b py-6 ${isRTL ? 'pr-8' : 'pl-8'} cursor-move`}>
                      <FaGripLines size={16} className="text-gray-400" />
                    </td>
                    <td className={`p-2 border-b py-6`}>
                      {formatNum((currentPage - 1) * limit + index + 1)}
                    </td>
                    <td className="p-2 border-b py-6">{book?.title || "-"}</td>
                    <td className="p-2 border-b py-6">
                      {book?.description?.slice(0, 50)}...
                    </td>
                    <td className="p-2 border-b py-6">{formatNum(book?.chapterCount || 0)}</td>
                    <td className="p-2 border-b py-6">
                      <div className="flex gap-4 items-center">
                        <AiOutlineEye
                          size={22}
                          className="cursor-pointer hover:opacity-50 text-blue-600"
                          onClick={() => getSingleBibleBookFunc(book?._id)}
                        />

                        <BiEditAlt
                          size={24}
                          className="cursor-pointer hover:opacity-50 text-green-600"
                          onClick={() => openModal("edit", book)}
                        />
                        <FaRegTrashCan
                          size={20}
                          className="cursor-pointer hover:opacity-50 text-red-500"
                          onClick={() => handleDelete(book._id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!initialLoad && books.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * limit + 1)} {t('pagination.to')}{" "}
              {formatNum(Math.min(currentPage * limit, totalRecords))} {t('pagination.of')} {formatNum(totalRecords)}{" "}
              {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {isViewOpen && selectedBookViewData && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[1200px] max-w-[95vw] relative" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex items-center justify-between  p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold">
                {selectedBookViewData?.title} - {t('bible.chapters')}
              </h2>
              <button
                onClick={handleCloseViewModal}
                className=" text-gray-500 hover:text-black"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="max-h-[70vh] overflow-y-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-[#FAFAFA] sticky top-0 z-10">
                    <tr className="text-sm font-semibold text-gray-700">
                      <th className={`w-32 p-2 py-4 border-b border-[#F7F7F9] ${isRTL ? 'pr-6' : 'pl-6'}`}>
                        {t('bible.chapter')}
                      </th>
                      <th className="p-2 py-4 border-b border-[#F7F7F9]">
                        {t('bible.sectionTitle')}
                      </th>
                      <th className="p-2 py-4 border-b border-[#F7F7F9]">
                        {t('bible.audio')}
                      </th>
                      <th className="p-2 py-4 border-b border-[#F7F7F9] text-center">
                        {t('bible.action')}
                      </th>
                    </tr>
                  </thead>

                {isViewDataLoading ? (
                  <tbody>
                    <tr>
                      <td colSpan={4} className="p-6 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {chapters?.map((chapter, index) => {
                      const isLastRow =
                        index === selectedBookViewData?.chapters?.length - 1;
                      const border = !isLastRow
                        ? "border-b border-[#F7F7F9]"
                        : "";
                      const isUploading = uploadingRows.has(index);

                      return (
                        <tr
                          key={chapter?.chapterNumber}
                          className={`text-sm font-medium transition-colors ${
                            isUploading
                              ? 'bg-yellow-50 opacity-75'
                              : dragOverIndex === index
                              ? 'bg-blue-50 border-2 border-blue-400'
                              : 'hover:bg-gray-50'
                          }`}
                          onDragOver={(e) => !isUploading && handleAudioDragOver(e, index)}
                          onDragLeave={(e) => !isUploading && handleAudioDragLeave(e, index)}
                          onDrop={(e) => !isUploading && handleAudioDrop(e, index)}
                        >
                          <td className={`p-2 py-4 ${isRTL ? 'pr-6' : 'pl-6'} ${border}`}>
                            {getChapterDisplayName(chapter.chapterNumber)}
                          </td>

                          <td className={`p-2 py-4 ${border}`}>
                            {chapter?.sections?.[0]}
                          </td>

                          <td className={`p-2 py-4 ${border}`}>
                            {chapter.audio ? (
                              <div className="flex flex-col gap-1">
                                {chapter.audioName && (
                                  <span className="text-xs text-gray-600 truncate max-w-[300px]">
                                    {chapter.audioName}
                                  </span>
                                )}
                                <audio
                                  controls
                                  src={chapter.audio}
                                  className="w-full h-10 min-w-[250px]"
                                >
                                  Your browser does not support the audio
                                  element.
                                </audio>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">
                                {t('bible.noAudio')}
                              </span>
                            )}
                          </td>

                          <td className={`p-2 py-4 text-center ${border}`}>
                            <div className="flex items-center justify-center gap-3">
                              {isUploading ? (
                                <div className="flex items-center gap-2 text-blue-600">
                                  <LoadingSpinner />
                                  <span className="text-xs">{t('bible.uploading') || 'Uploading...'}</span>
                                </div>
                              ) : (
                                <>
                                  <label className="cursor-pointer text-blue-600 hover:text-blue-700 inline-flex items-center gap-2">
                                    <IoCloudUploadOutline size={20} />
                                    <span className="text-xs">{chapter.audio ? (t('bible.replace') || 'Replace') : (t('bible.clickOrDrop') || 'Click or Drop')}</span>
                                    <input
                                      type="file"
                                      accept="audio/mpeg, audio/mp3"
                                      className="hidden"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          handleAudioUpload(
                                            index,
                                            e.target.files[0]
                                          );
                                        }
                                      }}
                                    />
                                  </label>
                                  {chapter.audio && (
                                    <>
                                      <button
                                        onClick={() => handleAudioDownload(index)}
                                        className="text-green-600 hover:text-green-700 transition-colors"
                                        title={t('bible.downloadAudio') || 'Download Audio'}
                                      >
                                        <FaDownload size={18} />
                                      </button>
                                      <button
                                        onClick={() => handleAudioDelete(index)}
                                        className="text-red-600 hover:text-red-700 transition-colors"
                                        title={t('bible.deleteAudio') || 'Delete Audio'}
                                      >
                                        <FaRegTrashCan size={18} />
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <dialog
        id="book_modal"
        className="modal transition-all shadow-[0px_1px_2px_0px_#0A0D120F]"
      >
        <div className="modal-box min-w-[800px] rounded-2xl p-0" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add"
                  ? t('bible.addBibleBook')
                  : t('bible.editBibleBook')}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Title */}
            <label className="block text-sm font-medium mb-2">{t('bible.bookTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleFieldChange("title", e.target.value)}
              placeholder={t('bible.enterBookTitle')}
              className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.title && (
              <p className="text-red-500 absolute -mt-[15px] text-xs">
                {errors.title}
              </p>
            )}

            {/* Select Language - Only show in edit mode to allow changing language */}
            {modalMode === "edit" && (
              <>
                <label className="block text-sm font-medium mb-2 mt-2">
                  {t('bible.changeLanguage') || 'Change Language'}
                </label>
                <select
                  className="w-full mb-4 border rounded-xl p-3 border-[#808080] bg-white capitalize"
                  value={language}
                  disabled={languagesOptionsArr.length === 0}
                  onChange={(e) => handleFieldChange("language", e.target.value)}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {languagesOptionsArr.map((lang) => (
                    <option className="capitalize" key={lang._id} value={lang._id}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {errors.language && (
                  <p className="text-red-500 absolute -mt-[15px] text-xs">
                    {errors.language}
                  </p>
                )}
              </>
            )}

            {/* USFM File Upload */}
            <label className="block text-sm font-medium mb-2 mt-2">{t('bible.usfmFile') || 'USFM File'}</label>
            <div className="flex flex-col items-start gap-3">
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                <IoMdAdd size={22} />
                <span className="text-sm font-medium">{t('bible.uploadUsfmFile') || 'Upload USFM File'}</span>
                <input
                  type="file"
                  accept=".usfm,.sfm,.txt"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              {pdfFile && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                  <img src={pdf} alt="file" className="w-6 h-6" />
                  <span className="text-sm font-medium">{pdfFile.name}</span>
                </div>
              )}
            </div>
            {errors.pdfFile && (
              <p className="text-red-500 text-xs mt-1">
                {errors.pdfFile}
              </p>
            )}

            {/* USFM Editor Button - Show when there's USFM content */}
            {usfmText && usfmText.length > 0 && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    // Close the book modal before opening USFM editor
                    document.getElementById("book_modal").close();
                    setIsUsfmEditorOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <BiEditAlt size={20} />
                  <span className="text-sm font-medium">{t('bible.editUsfm') || 'Edit USFM'}</span>
                </button>
              </div>
            )}
          </div>

          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              type="button"
              disabled={loading}
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
            >
              {loading ? (
                <LoadingSpinner />
              ) : modalMode === "add" ? (
                t('common.add')
              ) : (
                t('common.update')
              )}
            </button>
          </div>
        </div>
      </dialog>

      {/* USFM Editor Modal */}
      {isUsfmEditorOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl w-[95vw] h-[90vh] max-w-[1400px] flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold">{t('bible.editUsfm') || 'Edit USFM'}</h2>
              <button
                onClick={() => {
                  setIsUsfmEditorOpen(false);
                  // Reopen the book modal when closing USFM editor
                  document.getElementById("book_modal").showModal();
                }}
                className="text-gray-500 hover:text-black text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
              <EditorUsfm
                usfmText={usfmText}
                setUsfmText={setUsfmText}
              />
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-[#fafafa]">
              <button
                onClick={() => {
                  setIsUsfmEditorOpen(false);
                  // Reopen the book modal when closing USFM editor
                  document.getElementById("book_modal").showModal();
                }}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t('common.close') || 'Close'}
              </button>
              <button
                onClick={async () => {
                  setIsUsfmEditorOpen(false);
                  await handleUpdate();
                }}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : (t('common.save') || 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
